// Scrollable rail listing every review unit in the current batch with its
// resolved status. Field units of a split confirmation are grouped under a
// non-clickable header row for their parent proposal. The active unit is
// highlighted; clicking a row jumps to it.
import React from 'react';
import classNames from 'classnames';
import {
  SharedIcon,
  type SharedIconProps,
} from 'components/SharedIcon/SharedIcon';
import type { MaterialSymbols } from 'material-design-icons-literal-types';
import { getFieldLabel } from '../../confirmationFieldConfig';
import type {
  ItemState,
  ItemStatus,
  PendingConfirmation,
  ReviewUnit,
} from '../types';
import { itemStatus } from '../useReviewQueue';

export const STATUS_META: Record<
  Exclude<ItemStatus, 'pending'>,
  { icon: MaterialSymbols; color: SharedIconProps['color']; label: string }
> = {
  approved: { icon: 'check_circle', color: 'success', label: 'Approved' },
  rejected: { icon: 'cancel', color: 'danger', label: 'Rejected' },
  edited: { icon: 'edit', color: 'warning', label: 'Edit suggested' },
};

interface Props {
  items: PendingConfirmation[];
  units: ReviewUnit[];
  decisions: Record<number, ItemState>;
  activeIndex: number;
  decidedCount: number;
  onSelect: (index: number) => void;
}

const entityOf = (item: PendingConfirmation): string | undefined =>
  typeof item.args?.entity === 'string'
    ? (item.args.entity as string)
    : undefined;

export const BatchNavigator: React.FC<Props> = ({
  items,
  units,
  decisions,
  activeIndex,
  decidedCount,
  onSelect,
}) => (
  <nav
    className="tw:min-h-0 tw:shrink-0 tw:basis-[210px] tw:overflow-y-auto tw:border-r tw:border-[color:var(--border-neutral)] tw:pr-2"
    aria-label="Proposed changes"
  >
    <div className="tw:mb-1.5 tw:text-[11px] tw:uppercase tw:tracking-[0.03em] tw:text-[color:var(--text-weak)]">
      {decidedCount} of {units.length} decided
    </div>
    <ul className="tw:m-0 tw:list-none tw:p-0">
      {units.map((unit, i) => {
        const item = items[unit.confirmationIndex];
        const status = itemStatus(decisions[i]);
        const meta = status === 'pending' ? null : STATUS_META[status];
        const isFieldUnit = unit.field !== undefined;
        // Header row above the first field unit of each split confirmation.
        const isGroupStart =
          isFieldUnit &&
          units[i - 1]?.confirmationIndex !== unit.confirmationIndex;
        const label = isFieldUnit
          ? getFieldLabel(item.tool_name, entityOf(item), unit.field!)
          : item.preview?.action || item.tool_name;
        return (
          <li key={`nav-${i}`}>
            {isGroupStart && (
              <div className="tw:overflow-hidden tw:text-ellipsis tw:whitespace-nowrap tw:px-2 tw:pb-0.5 tw:pt-2 tw:text-[11px] tw:uppercase tw:tracking-[0.03em] tw:text-[color:var(--text-weak)]">
                {item.preview?.action || item.tool_name}
              </div>
            )}
            {/* Rows keep a constant height whether decided or not: the status
                icon renders inside the same fixed 20px slot as the undecided
                number badge, and the status is conveyed by icon + tooltip
                (with sr-only text) rather than a stacked sub-line — a second
                line would grow the rail on every decision. */}
            <button
              type="button"
              title={meta?.label}
              className={classNames(
                'tw:flex tw:w-full tw:cursor-pointer tw:items-start tw:gap-2 tw:rounded-md tw:border-none tw:bg-transparent tw:px-2 tw:py-[7px] tw:text-left tw:text-[13px] tw:text-[color:var(--text-white)] tw:hover:bg-[color:var(--background-light)]',
                {
                  'tw:bg-[rgba(2,163,254,0.18)] tw:font-medium':
                    i === activeIndex,
                  'tw:pl-4': isFieldUnit,
                }
              )}
              onClick={() => onSelect(i)}
            >
              <span className="tw:mt-px tw:flex tw:h-5 tw:shrink-0 tw:basis-[22px] tw:items-center tw:justify-center">
                {meta ? (
                  <SharedIcon
                    iconName={meta.icon}
                    size="sm"
                    color={meta.color}
                    classes="tw:!leading-5"
                  />
                ) : (
                  <span className="tw:flex tw:h-5 tw:w-5 tw:items-center tw:justify-center tw:rounded-[5px] tw:bg-[color:var(--neutral-600)] tw:text-[11px] tw:text-[color:var(--text-white)]">
                    {i + 1}
                  </span>
                )}
              </span>
              <span className="tw:flex tw:min-w-0 tw:flex-1 tw:flex-col">
                <span className="tw:overflow-hidden tw:text-ellipsis tw:whitespace-nowrap">
                  {label}
                </span>
                {meta && <span className="tw:sr-only">{meta.label}</span>}
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  </nav>
);

export default BatchNavigator;
